import React from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const FilterPanel = ({ filters, onFiltersChange, totalRecommendations }) => {
  
  const roleOptions = [
    { value: 'all', label: 'Tous les rôles' },
    { value: 'agent', label: 'Agents' },
    { value: 'venue', label: 'Venues' },
    { value: 'booker', label: 'Bookers' },
    { value: 'independent', label: 'Artistes indépendants' }
  ];

  const locationOptions = [
    { value: 'all', label: 'Toutes les villes' },
    { value: 'Paris', label: 'Paris' },
    { value: 'Lyon', label: 'Lyon' },
    { value: 'Marseille', label: 'Marseille' },
    { value: 'Toulouse', label: 'Toulouse' },
    { value: 'Nice', label: 'Nice' },
    { value: 'Nantes', label: 'Nantes' },
    { value: 'Montpellier', label: 'Montpellier' },
    { value: 'Strasbourg', label: 'Strasbourg' },
    { value: 'Bordeaux', label: 'Bordeaux' },
    { value: 'Lille', label: 'Lille' }
  ];

  const professionOptions = [
    { value: 'all', label: 'Toutes les professions' },
    { value: 'musician', label: 'Musicien' },
    { value: 'singer', label: 'Chanteur' },
    { value: 'dj', label: 'DJ' },
    { value: 'producer', label: 'Producteur' },
    { value: 'model', label: 'Modèle' },
    { value: 'actor', label: 'Acteur' },
    { value: 'dancer', label: 'Danseur' },
    { value: 'performer', label: 'Artiste' }
  ];

  const experienceOptions = [
    { value: 'all', label: 'Tous les niveaux' },
    { value: 'beginner', label: 'Débutant (0-2 ans)' },
    { value: 'intermediate', label: 'Intermédiaire (2-5 ans)' },
    { value: 'expert', label: 'Expert (5+ ans)' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange?.({
      ...filters,
      [key]: value
    });
  };

  const resetFilters = () => {
    onFiltersChange?.({
      role: 'all',
      location: 'all',
      profession: 'all',
      experienceLevel: 'all',
      verifiedOnly: false
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters?.role !== 'all') count++;
    if (filters?.location !== 'all') count++;
    if (filters?.profession !== 'all') count++;
    if (filters?.experienceLevel !== 'all') count++;
    if (filters?.verifiedOnly) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Filtres</h3>
        {activeFiltersCount > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">
              {activeFiltersCount}
            </span>
            <Button
              variant="ghost"
              size="xs"
              onClick={resetFilters}
              className="text-muted-foreground hover:text-foreground"
              iconName="X"
            >
              Effacer
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Role Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Type de profil
          </label>
          <Select
            value={filters?.role}
            onChange={(value) => handleFilterChange('role', value)}
            options={roleOptions}
            placeholder="Sélectionner un rôle"
          />
        </div>

        {/* Location Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Localisation
          </label>
          <Select
            value={filters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            options={locationOptions}
            placeholder="Sélectionner une ville"
          />
        </div>

        {/* Profession Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Profession artistique
          </label>
          <Select
            value={filters?.profession}
            onChange={(value) => handleFilterChange('profession', value)}
            options={professionOptions}
            placeholder="Sélectionner une profession"
          />
        </div>

        {/* Experience Level Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Niveau d'expérience
          </label>
          <Select
            value={filters?.experienceLevel}
            onChange={(value) => handleFilterChange('experienceLevel', value)}
            options={experienceOptions}
            placeholder="Sélectionner un niveau"
          />
        </div>

        {/* Verified Only Filter */}
        <div>
          <label className="flex items-center space-x-3 cursor-pointer">
            <Checkbox
              checked={filters?.verifiedOnly}
              onChange={(checked) => handleFilterChange('verifiedOnly', checked)}
            />
            <div>
              <div className="text-sm font-medium text-foreground">
                Profils vérifiés uniquement
              </div>
              <div className="text-xs text-muted-foreground">
                Afficher seulement les comptes certifiés
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="mt-6 pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-foreground mb-3">Filtres rapides</h4>
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => onFiltersChange?.({
              ...filters,
              role: 'agent',
              verifiedOnly: true
            })}
            iconName="Shield"
          >
            Agents vérifiés
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => onFiltersChange?.({
              ...filters,
              location: 'Paris',
              experienceLevel: 'expert'
            })}
            iconName="MapPin"
          >
            Experts parisiens
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => onFiltersChange?.({
              ...filters,
              profession: 'dj',
              role: 'independent'
            })}
            iconName="Music"
          >
            DJs indépendants
          </Button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Filter" size={14} />
          <span>{totalRecommendations} profils disponibles</span>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;